// import packages
import React, { useState, useContext, useEffect } from "react";
import { PageActions } from "@shopify/polaris";
import { useMutation } from "@apollo/react-hooks";

// import hoc
import { withFeature } from "lib/hoc";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

// import constant
import constant from "lib/constant/constant";

// import context
import { PrivateContext } from "lib/context/privateContext";

// import components
import { Sheet } from "lib/components";

// import provider
import { FormProvider } from "app/productOld/modules/operator/features/add/context/context";

// import gql
import { ADD_PRODUCT, ATTACH_UPLOAD, UPLOAD_IMAGE, UPLOAD_FILE } from "app/productOld/apollo/mutations";

// import props
import { manualFormProp } from "app/productOld/modules/operator/features/add/props";
import {
  Attachment,
  Image,
  Inventory,
  Organisation,
  Price,
  Shipping,
  TitleDescription,
  Variant,
} from "./subFeatureItems";

const { SHOPIFY, OPTIONS } = constant;

const ManualForm = (props) => {
  const { setBanner } = props;
  const {
    gql,
    value: { MAX_MEASUREMENT = 999 },
  } = constant;
  const { history, currentUser, cms } = useContext(PrivateContext);
  const { aws } = currentUser;
  const { bucket = "", path = "", id = "", secret = "" } = aws || {};
  const [value, setValue] = useState({
    shipping: true,
    policySelection: SHOPIFY,
  });
  const [sheetActive, setSheetActive] = useState(false);
  const [sheetTitle, setSheetTitle] = useState("");
  const [sheetContent, setSheetContent] = useState("");
  const [imageStartUploading, setImageStartUploading] = useState(false);
  const [fileStartUploading, setFileStartUploading] = useState(false);
  const [mcProductId, setMcProductId] = useState(" ");
  const [uploads, setUploads] = useState({ toBeUploadedFile: null, toBeUploadedImage: null });
  const shopify = constant.SHOPIFY;
  const [uploadImage, { loading: uploadImageLoading }] = useMutation(UPLOAD_IMAGE);
  const [uploadFile, { loading: uploadFileLoading }] = useMutation(UPLOAD_FILE);
  const [addProductItem, { loading: addProductLoading }] = useMutation(ADD_PRODUCT);
  const [attachUpload, { loading: attachLoading }] = useMutation(ATTACH_UPLOAD);
  const primaryAction = {
    content: cms("common.label.done"),
    onAction: () => setSheetActive(false),
  };

  const secondaryAction = {
    content: cms("common.button.cancel"),
    onAction: () => setSheetActive(false),
  };

  const handleChange = (fieldName, fieldValue) => {
    setValue({
      ...value,
      [fieldName]: fieldValue,
    });
  };

  const handleTiny = (content) => {
    handleChange(constant.TINYMCE, content);
  };

  const learnMore = (productItem, title) => {
    setSheetActive(true);
    setSheetTitle(title);
    setSheetContent(`TODO: Add description about ${productItem}`);
  };

  const addProduct = async (formValues) => {
    try {
      const val = await addProductItem({
        variables: {
          input: formValues,
        },
      });

      const resData = baseHelper.getResponseData(val.data, constant.gql.ADD_PRODUCT);

      const resError = baseHelper.getResponseError(val.data, constant.gql.ADD_PRODUCT);
      if (resError) {
        const banner = {
          action: null,
          isOpen: true,
          status: constant.CRITICAL,
          title: resError,
        };
        setBanner(banner);
        return null;
      }
      return resData;
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
    return null;
  };
  const findVendorName = (options, selectedValue) => {
    const result = options.find((vendorOption) => vendorOption.value === selectedValue);
    return result;
  };

  useEffect(() => {
    if (uploads.toBeUploadedFile !== null && uploads.toBeUploadedImage !== null) {
      attachUpload({
        variables: {
          input: {
            _id: mcProductId,
            images: uploads.toBeUploadedImage || [],
            attachments: uploads.toBeUploadedFile || [],
          },
        },
      })
        .then((res) => {
          const responseError = baseHelper.getResponseError(res.data, gql.ATTACH_UPLOAD);
          const responseData = baseHelper.getResponseData(res.data, gql.ATTACH_UPLOAD);
          if (responseError) {
            setBanner({ isOpen: true, status: constant.CRITICAL, title: responseError });
          }
          if (responseData) {
            history.push(`edit/${encodeURIComponent(mcProductId)}`, { add: true });
          }
        })
        .catch((exception) => {
          setBanner({ isOpen: true, status: constant.CRITICAL, title: errorHelper.parse(exception) });
        });
    }
  }, [uploads]);

  const imageUpload = (images = [], productId = "") => {
    let hasImageError = false;
    const uploadedImages = [];
    if (images.length === 0) {
      setUploads((prevState) => {
        return { ...prevState, toBeUploadedImage: [] };
      });
    } else if (images && images.length) {
      const uploadImageCallback = (imageResponse) => {
        const { imageUrl = null, imageId = null } = imageResponse;
        if (imageUrl) {
          const uploadedImageData = {
            imageUrl,
            imageId,
          };

          uploadedImages.push(uploadedImageData);
        }
        if (hasImageError) {
          return;
        }
        if (uploadedImages && uploadedImages.length > 0 && uploadedImages.length === images.length) {
          setImageStartUploading(false);
          setUploads((prevState) => {
            return { ...prevState, toBeUploadedImage: uploadedImages };
          });
        }
      };

      images.map(async (img) => {
        try {
          if (hasImageError) {
            return;
          }
          const response = await uploadImage({ variables: { input: { image: img, productId } } });
          if (!imageStartUploading) {
            setImageStartUploading(true);
          }
          const imageResponse = baseHelper.getResponseData(response.data, gql.UPLOAD_IMAGE);
          const imageError = baseHelper.getResponseError(response.data, gql.UPLOAD_IMAGE);
          if (imageError) {
            setImageStartUploading(false);
            hasImageError = true;
            setBanner((prev) => ({
              ...prev,
              action: null,
              isOpen: true,
              status: constant.CRITICAL,
              title: imageError,
            }));
          }
          uploadImageCallback(imageResponse);
        } catch (uploadImageError) {
          setImageStartUploading(false);
          hasImageError = true;
          setBanner((prev) => ({
            ...prev,
            action: null,
            isOpen: true,
            status: constant.CRITICAL,
            title: cms("section.form.message.error.imageNotSaved"),
          }));
        }
      });
      setImageStartUploading(false);
    }
    return null;
  };

  const submitCallBack = async () => {
    const {
      title,
      tinyMCE,
      image: formImage,
      file: fromAttachment,
      price,
      comparePrice,
      sku,
      barcode,
      policySelection,
      inventoryQuantity,
      shipping,
      weight,
      weightUnit,
      vendorValue,
      productValueType,
      organisationTags,
      variant,
      vendorOptions,
      length = 0,
      width = 0,
      height = 0,
    } = value;
    const option1 = (variant && variant.length && variant[0].option1) || constant.SIZE;
    const option2 = (variant && variant.length && variant[0].option2) || constant.COLOR;
    const option3 = (variant && variant.length && variant[0].option3) || constant.MATERIAL;
    const vendorName = findVendorName(vendorOptions, vendorValue);
    const vendor = vendorName.label;
    const vendorId = vendorValue;

    const measurement = {
      length: {
        value: parseFloat(length),
        unit: constant.CM,
      },
      width: {
        value: parseFloat(width),
        unit: constant.CM,
      },
      height: {
        value: parseFloat(height),
        unit: constant.CM,
      },
    };

    const formValues = {
      title,
      description: tinyMCE,
      vendor,
      vendorId,
      tags: organisationTags,
      productType: productValueType,
      barcode,
      inventoryManagement: policySelection === shopify ? shopify : null,
      sku,
      isShipping: !!shipping,
      weightUnit: weightUnit || OPTIONS[0],
      fulfillmentService: constant.MANUAL,
      option1,
      option2,
      option3,
      images: formImage,
      attachments: fromAttachment,
      variants: variant || [],
      measurement,
    };

    formValues.comparePrice = parseFloat(comparePrice);
    formValues.price = parseFloat(price);
    formValues.weight = parseFloat(weight);
    formValues.quantity = parseInt(inventoryQuantity, 10);
    // images
    const tempImages = formValues.images && [...formValues.images];
    formValues.images = [];
    // attachment
    const tempAttachments = formValues.attachments ? [...formValues.attachments] : [];
    formValues.attachments = [];
    try {
      // add product
      const productId = await addProduct(formValues);
      setMcProductId(productId);
      if (productId && ((tempImages && tempImages.length) || (tempAttachments && tempAttachments.length))) {
        // save attachments
        // eslint-disable-next-line no-use-before-define
        fileUpload(tempAttachments, productId);

        // save images
        imageUpload(tempImages, productId);
      } else if (productId) {
        history.push(`edit/${encodeURIComponent(productId)}`, { add: true });
      }
    } catch (exception) {
      // delete the product from the DB if it doest save
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };

  const fileUpload = (tempAttachments = [], productId = "") => {
    let hasFileError = false;
    const uploadedFiles = [];
    if (tempAttachments.length === 0) {
      setUploads((prevState) => {
        return { ...prevState, toBeUploadedFile: [] };
      });
    } else if (tempAttachments && tempAttachments.length) {
      if (!fileStartUploading) {
        setFileStartUploading(true);
      }

      const uploadFileCallback = (fileResponse) => {
        const { fileURL = null, fileId = null } = fileResponse;
        if (fileURL) {
          const uploadedFileData = {
            fileURL,
            fileId,
          };
          uploadedFiles.push(uploadedFileData);
        }
        if (hasFileError) {
          return;
        }
        if (uploadedFiles && uploadedFiles.length > 0 && uploadedFiles.length === tempAttachments.length) {
          setFileStartUploading(false);
          setUploads((prevState) => {
            return { ...prevState, toBeUploadedFile: uploadedFiles };
          });
        }
      };

      tempAttachments.map(async (file) => {
        try {
          if (hasFileError) {
            return;
          }
          const response = await uploadFile({ variables: { input: { file, productId } } });
          if (!fileStartUploading) {
            setFileStartUploading(true);
          }
          const fileResponse = baseHelper.getResponseData(response.data, gql.UPLOAD_FILE);
          const fileError = baseHelper.getResponseError(response.data, gql.UPLOAD_FILE);
          if (fileError) {
            setFileStartUploading(false);
            hasFileError = true;
            setBanner((prev) => ({
              ...prev,
              action: null,
              isOpen: true,
              status: constant.CRITICAL,
              title: fileError,
            }));
          }
          uploadFileCallback(fileResponse);
        } catch (uploadFileError) {
          setFileStartUploading(false);
          hasFileError = true;
          setBanner((prev) => ({
            ...prev,
            action: null,
            isOpen: true,
            status: constant.CRITICAL,
            title: cms("section.form.message.error.fileNotSaved"),
          }));
        }
      });
      setFileStartUploading(false);
    }
    return null;
  };

  const onFormSubmit = () => {
    let banner = {
      action: null,
      isOpen: false,
      status: "",
      title: "",
    };
    if (!value.title || value.title.trim() === "") {
      banner = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("section.form.message.error.titleIsRequired"),
      };
      setBanner(banner);
      return;
    }
    if (!value.vendorValue || value.vendorValue.trim() === "") {
      banner = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("section.form.message.error.vendorIsRequired"),
      };
      setBanner(banner);
      return;
    }

    let { length = 0, width = 0, height = 0 } = value;
    length = parseFloat(length);
    width = parseFloat(width);
    height = parseFloat(height);

    if (length > MAX_MEASUREMENT || width > MAX_MEASUREMENT || height > MAX_MEASUREMENT) {
      banner = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("section.form.message.error.invalidMeasurement"),
      };
      setBanner(banner);
      return;
    }

    // check volumetric weight is greater than 180 or not if yes then through error
    const isLargeVolumetricWeight = baseHelper.isLargeVolumetricWeight(length, width, height);
    if (isLargeVolumetricWeight) {
      banner = {
        action: null,
        isOpen: true,
        status: constant.CRITICAL,
        title: cms("section.form.message.error.largeVolumetricWeight"),
      };
      setBanner(banner);
      return;
    }
    submitCallBack();
  };

  return (
    <>
      <FormProvider value={{ handleChange, handleTiny, data: value, learnMore }}>
        <TitleDescription />
        <Image />

        {bucket && path && id && secret && <Attachment />}
        <Price />
        <Inventory />
        <Shipping />
        <Variant />
        <Organisation />
      </FormProvider>

      <PageActions
        primaryAction={{
          id: "submitButton",
          content: cms("common.button.submit"),
          onAction: () => onFormSubmit(),
          loading:
            attachLoading ||
            imageStartUploading ||
            uploadImageLoading ||
            uploadFileLoading ||
            addProductLoading ||
            false,
        }}
        secondaryActions={[
          {
            id: "cancelButton",
            onAction: () => history.push("/"),
            content: cms("common.button.cancel"),
          },
        ]}
      />
      <Sheet
        title={sheetTitle}
        isOpen={sheetActive}
        onClose={() => setSheetActive(false)}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        {sheetContent}
      </Sheet>
    </>
  );
};

ManualForm.propTypes = manualFormProp.type;

export default withFeature(ManualForm, { feature: constant.PRODUCT_USING_FORM });
